/**
 * Los motivos de la sección de la página web, en el idioma del dueño (D6).
 *
 * Viven aparte de `SeccionDeLaPagina.tsx` por tamaño (D4) y porque son la
 * única parte que traduce códigos del servidor a texto: probarlos no
 * necesita montar nada.
 */

import type { MotivoDeDescarte, PaginaLeidaResumen } from '@chatbot/shared-types';
import { textos } from './textos';
import { paginas } from './textos-web';

type Motivo = keyof typeof textos.conocimiento.porQueNoSePudo;

/** El motivo del rastreador, en el idioma del dueño. Nunca nuestra jerga en su pantalla. */
export function explicar(motivo: string | undefined): string {
  const tabla = textos.conocimiento.porQueNoSePudo;
  return motivo && motivo in tabla ? tabla[motivo as Motivo] : tabla.otro;
}

/**
 * El status del error.
 *
 * `request` (`peticion.ts`) lanza `new Error('400: …')` SIN propiedad
 * `status`: leer sólo la propiedad hacía que en producción todo fuera
 * «problema nuestro» y el dueño nunca viera «esa no parece una dirección»
 * (revisión del 2026-09-24, ronda 2). Se lee de las dos formas.
 */
function statusDe(err: unknown): number | undefined {
  if (typeof err !== 'object' || err === null) return undefined;
  const propio = Reflect.get(err, 'status');
  if (typeof propio === 'number') return propio;
  const m = /^(\d{3}):/.exec(String(Reflect.get(err, 'message') ?? ''));
  return m ? Number(m[1]) : undefined;
}

/**
 * Nuestros nombres de campo. El mismo endpoint contesta 400 con «tenantId
 * inválido» y con «url inválida», y mostrarlos tal cual le pone al dueño un
 * nombre de variable en la pantalla (D6, ronda 3 M1).
 */
const JERGA = /tenantId|url inv[áa]lida/i;
/** Más que esto no es un motivo, es un volcado. */
const MAX_MOTIVO = 140;

/**
 * Lo que el servidor dijo, si se le puede mostrar al dueño: sin el prefijo
 * del status, sin jerga nuestra, y como una frase y no como un renglón de
 * log (mayúscula al principio y punto al final, ronda 3 M2). Cadena vacía
 * cuando no sirve, y entonces gana nuestro copy.
 */
function loQueDijoElServidor(err: unknown): string {
  const mensaje = typeof err === 'object' && err !== null ? Reflect.get(err, 'message') : '';
  const dicho = String(mensaje ?? '')
    .replace(/^\d{3}:\s*/, '')
    .trim();
  if (dicho.length === 0 || JERGA.test(dicho)) return '';
  const corto = dicho.length > MAX_MOTIVO ? `${dicho.slice(0, MAX_MOTIVO - 1)}…` : dicho;
  const conPunto = /[.!?…]$/.test(corto) ? corto : `${corto}.`;
  return conPunto.charAt(0).toUpperCase() + conPunto.slice(1);
}

/**
 * Por qué no se pudo ni encolar.
 *
 * Que falte una variable nuestra, que la sesión haya vencido o que se caiga la
 * red no son «tu dirección está mal». Decírselo así lo manda a corregir en loop
 * una URL que estaba bien.
 */
export function porQueNoArranco(err: unknown): string {
  const t = textos.conocimiento.noSePudoEmpezar;
  const status = statusDe(err);
  if (status === 400) return t.url;
  if (status === 401 || status === 403) return t.sesion;
  return t.nuestro;
}

/**
 * Por qué no se pudo pedir más (GKTIBO-136).
 *
 * Un 400 acá no es «tu dirección»: es el pedido (otro dominio, más de veinte).
 * Y un 409 es que el rastreo anterior sigue corriendo.
 */
export function porQueNoSePudoPedir(err: unknown): string {
  const status = statusDe(err);
  if (status === 409) return paginas.rastreoEnCurso;
  // El API ya contesta en castellano y con el motivo exacto («no es una
  // página de tu sitio», «demasiado larga»): «probá con menos» no arregla
  // ninguno de esos (m11). Si no dijo nada, el genérico.
  if (status === 400) return loQueDijoElServidor(err) || paginas.noSePudoPedir;
  if (status === 401 || status === 403) return textos.conocimiento.noSePudoEmpezar.sesion;
  return textos.conocimiento.noSePudoEmpezar.nuestro;
}

export interface Descartada {
  url: string;
  motivo: MotivoDeDescarte;
}

/** Sólo lo que quedó afuera por nuestra decisión se puede pedir. */
const PEDIBLE: Record<MotivoDeDescarte, boolean> = {
  tope: true,
  despriorizada: true,
  'otro-idioma': false,
};

export function esPedible(d: Descartada): boolean {
  return PEDIBLE[d.motivo];
}

/** Dos trabajos pueden leer la misma página; en la lista va una vez. */
export function sinRepetir<T extends { url: string }>(paginas: T[]): T[] {
  const vistas = new Set<string>();
  return paginas.filter((p) => {
    if (vistas.has(p.url)) return false;
    vistas.add(p.url);
    return true;
  });
}

/**
 * Lo que sigue afuera después de un pedido: lo de antes menos lo pedido y
 * menos lo que ya se leyó. Sin esto, pedir 20 de 64 hacía desaparecer las
 * otras 44 (revisión del 2026-09-24, I2).
 */
export function loQueSigueAfuera(afuera: Descartada[], leidas: PaginaLeidaResumen[]): Descartada[] {
  const yaLeidas = new Set(leidas.map((l) => l.url));
  return sinRepetir(afuera).filter((d) => !yaLeidas.has(d.url));
}
